import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, Package, X, ShoppingBag, Menu } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const API_URL = `${BACKEND_URL}/api/admin`;

export const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Modal & Form State (Simplified for display)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({});

  // --- NEW: Open Modal and Prep Data ---
  const openEditModal = (product) => {
    setEditingProduct(product);
    // Convert array of objects [{id, name: 'Summer'}, {id, name: 'Trend'}] -> String "Summer, Trend"
    const currentTags = product.tags.map(t => t.name).join(', ');
    setFormData({
      tags: currentTags,
      name: product.name,
      price: product.price
    });
    setIsModalOpen(true);
  };

  // --- NEW: Save Changes to Backend ---
  const handleSaveChanges = async () => {
    if (!editingProduct) return;

    try {
      // Split string back into array for backend
      const tagArray = formData.tags.split(',').map(t => t.trim()).filter(t => t);

      // Call the PUT endpoint we created earlier
      const res = await axios.put(
        `${API_URL}/products/${editingProduct.id}`,
        { tags: tagArray },
        getAuthHeader()
      );

      // Update local state so UI changes instantly
      setProducts(products.map(p => p.id === editingProduct.id ? res.data : p));

      toast.success("Product Updated! It will now appear in the new categories.");
      setIsModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to update product");
    }
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'products') {
        const res = await axios.get(`${API_URL}/products`, getAuthHeader());
        setProducts(res.data);
      } else if (activeTab === 'users') {
        const res = await axios.get(`${API_URL}/users`, getAuthHeader());
        setUsers(res.data);
      } else {
        const res = await axios.get(`${API_URL}/orders`, getAuthHeader());
        setOrders(res.data);
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      if (error.response && error.response.status === 403) {
        toast.error("Access Denied. You are not an Admin.");
      } else {
        toast.error("Failed to fetch data.");
      }
    } finally {
      setLoading(false);
    }
  }, [activeTab]); // Depend only on activeTab

  useEffect(() => {
    fetchData();
  }, [fetchData]); // Now it's safe to add fetchData here

  // ... (Keep your Handlers: handleAddNewClick, handleEditClick, handleDelete, etc.) ...
  const handleAddNewClick = () => { setIsModalOpen(true); };
  const handleEditClick = (p) => { setIsModalOpen(true); };
  const handleDelete = () => { toast.error("Backend delete logic required for Woo"); };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">

      {/* MOBILE HEADER */}
      <div className="md:hidden bg-black text-white p-4 flex justify-between items-center sticky top-0 z-30 ">
        <h2 className="text-xl font-bold text-[#F4E06D]">Admin Panel</h2>
        <button onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
          {isSidebarOpen ? <X /> : <Menu />}
        </button>
      </div>

      {/* SIDEBAR */}
      <aside className={`
        fixed inset-y-0 left-0 sm:z-30 z-50 w-64 bg-black text-white p-6 transform transition-transform duration-300 ease-in-out
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        md:relative md:translate-x-0 md:block
      `}>
        <div className="hidden md:block mb-8">
          <h2 className="text-2xl font-bold text-[#F4E06D]">Admin Panel</h2>
        </div>
        <nav className="space-y-4">
          <button onClick={() => { setActiveTab('products'); setIsSidebarOpen(false); }} className={`flex items-center gap-3 w-full p-3 rounded transition ${activeTab === 'products' ? 'bg-[#F4E06D] text-black font-bold' : 'hover:bg-gray-800'}`}>
            <Package size={20} /> Products
          </button>
          <button onClick={() => { setActiveTab('users'); setIsSidebarOpen(false); }} className={`flex items-center gap-3 w-full p-3 rounded transition ${activeTab === 'users' ? 'bg-[#F4E06D] text-black font-bold' : 'hover:bg-gray-800'}`}>
            <Users size={20} /> Customers
          </button>
          <button onClick={() => { setActiveTab('orders'); setIsSidebarOpen(false); }} className={`flex items-center gap-3 w-full p-3 rounded transition ${activeTab === 'orders' ? 'bg-[#F4E06D] text-black font-bold' : 'hover:bg-gray-800'}`}>
            <ShoppingBag size={20} /> Orders
          </button>
        </nav>
      </aside>

      {/* OVERLAY */}
      {isSidebarOpen && <div className="fixed inset-0 bg-black/50 z-20 md:hidden" onClick={() => setIsSidebarOpen(false)} />}

      {/* MAIN CONTENT */}
      <main className="flex-1 p-4 md:p-8 overflow-y-auto h-[calc(100vh-64px)] md:h-screen">
        <h1 className="text-2xl md:text-3xl font-bold capitalize mb-6">{activeTab} (WooCommerce)</h1>

        {loading ? (
          <div className="flex justify-center p-12"><p>Loading...</p></div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">

            {/* --- PRODUCTS TABLE (Raw Woo Data) --- */}
            {/* --- PRODUCTS TABLE (Raw Woo Data) --- */}
            {activeTab === 'products' && (
              <table className="w-full text-left text-sm min-w-[600px]">
                <thead className="bg-gray-100 border-b">
                  <tr>
                    <th className="p-4 font-semibold">Image</th>
                    <th className="p-4 font-semibold">Name</th>
                    <th className="p-4 font-semibold">Tags (Categories)</th> {/* Changed Header */}
                    <th className="p-4 font-semibold">Price</th>
                    <th className="p-4 font-semibold">Action</th> {/* New Column */}
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-b hover:bg-gray-50">
                      <td className="p-4">
                        <img src={p.images?.[0]?.src} alt="" className="w-10 h-10 rounded object-cover border" />
                      </td>
                      <td className="p-4 font-medium max-w-[200px] truncate" title={p.name}>
                        {p.name}
                      </td>
                      {/* Show current tags so you know where it belongs */}
                      <td className="p-4 text-xs text-gray-500 max-w-[150px]">
                        {p.tags.map(t => t.name).join(', ')}
                      </td>
                      <td className="p-4">₹{p.price}</td>
                      <td className="p-4">
                        {/* The Edit Button */}
                        <button
                          onClick={() => openEditModal(p)}
                          className="bg-black text-white px-3 py-1 rounded text-xs hover:bg-gray-800"
                        >
                          Edit Tags
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* --- CUSTOMERS TABLE (Raw Woo Data) --- */}
            {activeTab === 'users' && (
              <table className="w-full text-left text-sm min-w-[600px]">
                <thead className="bg-gray-100 border-b">
                  <tr>
                    <th className="p-4 font-semibold">Name</th>
                    <th className="p-4 font-semibold">Email</th>
                    <th className="p-4 font-semibold">Role</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b hover:bg-gray-50">
                      <td className="p-4 font-medium">
                        {u.first_name} {u.last_name}
                        {(!u.first_name && !u.last_name) && u.username}
                      </td>
                      <td className="p-4 text-gray-600">{u.email}</td>
                      <td className="p-4">
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs capitalize">
                          {u.role}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* --- ORDERS TABLE (Raw Woo Data) --- */}
            {activeTab === 'orders' && (
              <table className="w-full text-left text-sm min-w-[800px]">
                <thead className="bg-gray-100 border-b">
                  <tr>
                    <th className="p-4 font-semibold">ID</th>
                    <th className="p-4 font-semibold">Customer</th>
                    <th className="p-4 font-semibold">Address</th>
                    <th className="p-4 font-semibold">Total</th>
                    <th className="p-4 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id} className="border-b hover:bg-gray-50">
                      <td className="p-4 font-medium">#{order.id}</td>
                      <td className="p-4">
                        {order.billing?.first_name} {order.billing?.last_name}<br />
                        <span className="text-xs text-gray-500">{order.billing?.email}</span>
                      </td>
                      <td className="p-4 text-xs text-gray-600 max-w-[200px]">
                        {order.shipping?.address_1}, {order.shipping?.city}
                      </td>
                      <td className="p-4">₹{order.total}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs font-bold capitalize bg-gray-100`}>
                          {order.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

          </div>
        )}
      </main>

      {/* Modal Placeholder */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">

            {/* Modal Header */}
            <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
              <h2 className="font-bold text-lg">Edit Categorization</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-black">
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                <input
                  disabled
                  value={formData.name || ''}
                  className="w-full bg-gray-100 border border-gray-300 rounded px-3 py-2 text-sm text-gray-500 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-900 mb-1">
                  Tags / Categories <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Use keywords like: <b>Summer, Anime, Trending, Festival, Winter</b> to assign this product to specific homepage sections.
                </p>
                <input
                  type="text"
                  value={formData.tags || ''}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="e.g. Summer, Anime, Cotton"
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-yellow-400 outline-none"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-gray-600 hover:text-black font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveChanges}
                className="bg-yellow-400 hover:bg-yellow-500 text-black px-6 py-2 rounded font-bold shadow-sm transition-all"
              >
                Save Changes
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};